import React from "react";
import { Mic, Tv, LogOut, Sparkles } from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { usePartyStore } from "../../stores/partyStore";
import { useIsMobile } from "../../hooks/useIsMobile";
import { logoutUser, linkAnonymousWithGoogle } from "../../lib/firebase";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { UserAvatar } from "./UserAvatar";

export const Header: React.FC = () => {
  const { user } = useAuthStore();
  const { currentParty, userRole, isTVMode, setTVMode } = usePartyStore();
  const isMobile = useIsMobile();

  const handleLinkGoogle = async () => {
    try {
      await linkAnonymousWithGoogle();
      alert("Successfully linked your Google Account! Your favorites are now saved permanently.");
    } catch (err: any) {
      alert(`Account link failed: ${err.message}`);
    }
  };

  return (
    <header className="bg-card/80 backdrop-blur-md border-b border-border sticky top-0 z-40 px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Brand */}
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/20">
            <Mic className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-wider leading-none text-text-main">
              SQUAD<span className="text-primary">-A-</span>ROKE
            </h1>
            <p className="text-[10px] text-text-muted uppercase tracking-widest font-semibold">
              Live Party Karaoke
            </p>
          </div>
        </div>

        {/* Actions & User */}
        <div className="flex items-center gap-2 sm:gap-3">
          {currentParty && !isMobile && (
            <button
              onClick={() => setTVMode(!isTVMode)}
              className={`px-3 py-1.5 rounded-xl border flex items-center gap-1.5 text-xs font-semibold transition-all cursor-pointer ${
                isTVMode
                  ? "bg-primary text-white border-primary glow-primary"
                  : "bg-card border-border hover:border-primary text-text-muted hover:text-text-main"
              }`}
              title="Toggle TV Display Mode"
            >
              <Tv className="w-4 h-4" />
              <span>TV Mode</span>
            </button>
          )}

          <ThemeSwitcher />

          {user && (
            <div className="flex items-center gap-2.5 pl-2 border-l border-border">
              <UserAvatar
                photoURL={user.photoURL}
                name={user.displayName || user.firstName}
                uid={user.uid}
                size="md"
                isHost={userRole === "HOST"}
              />

              <div className="hidden md:flex flex-col text-left">
                <span className="text-xs font-semibold leading-tight text-text-main">
                  {user.firstName}
                </span>
                {user.isAnonymous ? (
                  <button
                    onClick={handleLinkGoogle}
                    className="text-[10px] text-accent hover:underline flex items-center gap-0.5 cursor-pointer"
                    title="Save favorites permanently by linking Google"
                  >
                    <Sparkles className="w-2.5 h-2.5" /> Link Google
                  </button>
                ) : (
                  <span className="text-[10px] text-text-muted truncate max-w-[120px]">
                    {user.email}
                  </span>
                )}
              </div>

              <button
                onClick={() => logoutUser()}
                className="p-2 rounded-xl text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
